import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import * as LucideIcons from "lucide-react";
import { ArrowRight, Star, Camera, Film, Plane, Heart, Award, Sparkles, CheckCircle2, Quote, ChevronLeft, ChevronRight, Play } from "lucide-react";
import api, { HERO_VIDEO, HERO_FALLBACK, LOGO_URL } from "@/lib/api";
import { toast } from "sonner";

/* ---------------- Reusable section header (eyebrow + headline + body) ---------------- */
function SectionHeader({ content, defaults, align = "center", className = "" }) {
  const eyebrow = content?.eyebrow ?? defaults?.eyebrow;
  const headlineHtml = (content?.headline_html ?? defaults?.headlineHtml ?? "")
    .replace(/<em>/g, '<em class="text-[#D4AF37] not-italic">');
  const body = content?.body ?? defaults?.body;
  return (
    <div className={`${align === "center" ? "text-center" : ""} ${className}`}>
      {eyebrow && <div className="eyebrow">{eyebrow}</div>}
      <h2 className="font-serif text-4xl md:text-5xl font-light mt-4 tracking-tight leading-tight"
          dangerouslySetInnerHTML={{ __html: headlineHtml }} />
      {body && <p className={`mt-5 text-white/65 leading-relaxed ${align === "center" ? "max-w-2xl mx-auto" : ""}`}>{body}</p>}
    </div>
  );
}

/* ---------------- Hero ---------------- */
export function Hero({ heroVideo }) {
  return (
    <section className="relative min-h-[100vh] w-full overflow-hidden bg-black" data-testid="hero-section">
      <video
        autoPlay loop muted playsInline poster={HERO_FALLBACK}
        className="absolute inset-0 w-full h-full object-cover"
      >
        <source src={heroVideo || HERO_VIDEO} type="video/mp4" />
      </video>
      <div className="absolute inset-0 cine-overlay" />
      <div className="grain absolute inset-0" />

      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12 min-h-[100vh] flex flex-col justify-end pb-24 md:pb-32 pt-32">
        <div className="fade-up">
          <div className="flex items-center gap-3 mb-8">
            <span className="divider-gold" />
            <span className="eyebrow">Wedding Stories Told Honestly</span>
          </div>
          <h1 className="font-serif font-light text-5xl sm:text-6xl md:text-7xl lg:text-8xl tracking-tight leading-[0.95] max-w-5xl">
            Capturing <em className="text-[#D4AF37] not-italic">Timeless</em> Love Stories<br />
            Through Photography & Film
          </h1>
          <p className="mt-8 text-base md:text-lg text-white/75 max-w-2xl leading-relaxed font-light">
            Wedding photography and films created to preserve the moments and emotions
            you never want to forget — across Canada, the United States, and wherever your love story takes you.
          </p>
          <div className="mt-10 flex flex-wrap gap-4">
            <Link to="/portfolio" className="btn-gold" data-testid="hero-portfolio-btn">View Our Work <ArrowRight size={14} /></Link>
            <Link to="/contact#availability" className="btn-ghost" data-testid="hero-availability-btn">Check Availability</Link>
            <Link to="/contact" className="btn-ghost" data-testid="hero-quote-btn">Say Hello</Link>
          </div>
        </div>
      </div>

      <div className="absolute bottom-8 right-8 z-10 hidden md:flex items-center gap-3 text-white/60 text-xs tracking-widest uppercase">
        <span className="h-px w-10 bg-[#D4AF37]" />Scroll to discover
      </div>
    </section>
  );
}

/* ---------------- Who We Are ---------------- */
export function WhoWeAre({ content }) {
  const bullets = content?.bullets || ["Story-first approach","Quiet, candid coverage","Honest editing","Thoughtful planning","A team that listens","Memories for generations"];
  const headlineHtml = (content?.headline_html ||
    "A Small Team That Listens —<br/>Then Quietly Captures <em>Your Story</em>")
    .replace(/<em>/g, '<em class="text-[#D4AF37] not-italic">');
  return (
    <section className="section-pad bg-[#050505]" data-testid="who-we-are">
      <div className="max-w-[1400px] mx-auto grid lg:grid-cols-2 gap-16 lg:gap-24 items-center">
        <div className="relative hover-zoom">
          <img src={content?.image_url || "https://images.pexels.com/photos/31953126/pexels-photo-31953126.jpeg"}
               alt="Elite Pixels artistry" className="w-full h-[600px] object-cover" />
          <div className="absolute -bottom-8 -right-8 bg-[#D4AF37] text-black px-8 py-6 hidden md:block">
            <div className="font-serif text-5xl leading-none">{content?.stat_number || "10+"}</div>
            <div className="text-[0.65rem] tracking-[0.3em] uppercase mt-1">{content?.stat_label || "Years Capturing Love"}</div>
          </div>
        </div>
        <div>
          <div className="eyebrow">{content?.eyebrow || "Who We Are"}</div>
          <h2 className="font-serif text-4xl md:text-5xl font-light mt-4 mb-8 tracking-tight leading-tight"
              dangerouslySetInnerHTML={{ __html: headlineHtml }} />
          {(content?.body_1 || "Elite Pixels was started by Mujtaba Emal — a wedding photographer and filmmaker who believes your wedding day is more than a day. It is a story of love, family, and memories that deserve to be remembered honestly.").split("\n\n").map((p, i) => (
            <p key={i} className="text-white/70 leading-relaxed mb-5">{p}</p>
          ))}
          {content?.body_2 && content.body_2.split("\n\n").map((p, i) => (
            <p key={`b2-${i}`} className="text-white/70 leading-relaxed mb-5">{p}</p>
          ))}
          <div className="grid grid-cols-2 gap-5 mt-4">
            {bullets.map(x => (
              <div key={x} className="flex items-center gap-2 text-sm text-white/85">
                <CheckCircle2 size={14} className="text-[#D4AF37] shrink-0" />{x}
              </div>
            ))}
          </div>
          {content?.cta_label !== "" && (
            <Link to={content?.cta_link || "/about"} className="btn-gold mt-10" data-testid="about-cta">
              {content?.cta_label || "Our Story"} <ArrowRight size={14}/>
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Why Choose ---------------- */

export function WhyChoose({ content }) {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/why-choose").then(r => setItems(r.data)).catch(()=>{}); }, []);
  if (!items.length) return null;
  return (
    <section className="section-pad bg-[#0a0a0a] border-y border-white/5" data-testid="why-choose">
      <div className="max-w-[1400px] mx-auto">
        <div className="mb-20">
          <SectionHeader content={content}
            defaults={{ eyebrow: "Why Couples Choose Us", headlineHtml: "The Things That <em>Matter</em>" }} />
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-px bg-white/5">
          {items.map((it, i) => {
            const Icon = LucideIcons[it.icon] || LucideIcons.Star;
            return (
              <div key={it.id || i} className="bg-[#0a0a0a] p-10 hover:bg-[#111] transition-colors group" data-testid={`why-card-${i}`}>
                <Icon size={28} className="text-[#D4AF37] mb-6 group-hover:scale-110 transition-transform" />
                <h3 className="font-serif text-xl mb-3">{it.title}</h3>
                <p className="text-sm text-white/55 leading-relaxed">{it.description}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Featured Portfolio (filterable) ---------------- */
export function FeaturedPortfolio({ content }) {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("All");
  useEffect(() => { api.get("/portfolio").then(r => setItems(r.data)).catch(()=>{}); }, []);
  const cats = ["All", ...Array.from(new Set(items.map(i => i.category)))];
  const filtered = filter === "All" ? items : items.filter(i => i.category === filter);

  return (
    <section className="section-pad bg-[#050505]" data-testid="featured-portfolio">
      <div className="max-w-[1600px] mx-auto">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-10 gap-6">
          <SectionHeader content={content} align="left"
            defaults={{ eyebrow: "Featured Work", headlineHtml: "Moments We <em>Loved</em>" }} />
          <div className="flex flex-wrap gap-2">
            {cats.map(c => (
              <button key={c} onClick={() => setFilter(c)}
                data-testid={`portfolio-filter-${c.toLowerCase().replace(/\s/g,'-')}`}
                className={`px-4 py-2 text-[0.7rem] tracking-[0.18em] uppercase border transition ${filter === c ? "border-[#D4AF37] text-[#D4AF37]" : "border-white/15 text-white/60 hover:border-white/40"}`}>
                {c}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
          {filtered.slice(0, 9).map((p, i) => {
            const tile = (
              <>
                <img src={p.image_url} alt={p.title} className="w-full h-full object-cover" loading="lazy" />
                <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-0 hover:opacity-100 transition flex items-end p-5">
                  <div>
                    <div className="text-[0.6rem] tracking-[0.3em] uppercase text-[#D4AF37]">{p.category}</div>
                    <div className="font-serif text-lg mt-1">{p.title}</div>
                  </div>
                </div>
              </>
            );
            const className = `hover-zoom relative ${i % 5 === 0 ? "lg:row-span-2 lg:col-span-1 aspect-[3/4]" : "aspect-square"}`;
            const linkedGallery = Array.isArray(p.gallery_slugs) && p.gallery_slugs.length > 0 ? p.gallery_slugs[0] : null;
            return linkedGallery ? (
              <Link key={p.id} to={`/portfolio/${linkedGallery}`} className={`${className} block`} data-testid={`featured-portfolio-${i}`}>{tile}</Link>
            ) : (
              <div key={p.id} className={className} data-testid={`featured-portfolio-${i}`}>{tile}</div>
            );
          })}
        </div>
        <div className="text-center mt-12">
          <Link to="/portfolio" className="btn-ghost" data-testid="portfolio-cta">View Full Gallery <ArrowRight size={14}/></Link>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Featured Wedding Films ---------------- */
export function FeaturedFilms({ content }) {
  const [films, setFilms] = useState([]);
  useEffect(() => { api.get("/films").then(r => setFilms(r.data)).catch(()=>{}); }, []);
  const normalize = (raw) => {
    if (!raw) return "";
    const t = String(raw).trim();
    if (/^[A-Za-z0-9_-]{11}$/.test(t)) return t;
    const m = t.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/|v\/))([A-Za-z0-9_-]{11})/);
    return m ? m[1] : "";
  };
  return (
    <section className="section-pad bg-[#0a0a0a] border-y border-white/5" data-testid="featured-films">
      <div className="max-w-[1400px] mx-auto">
        <div className="mb-16">
          <SectionHeader content={content}
            defaults={{ eyebrow: "Wedding Films", headlineHtml: "Films That Bring You <em>Back</em>" }} />
        </div>
        <div className="grid md:grid-cols-2 gap-6">
          {films.slice(0, 4).map(f => {
            const ytId = normalize(f.youtube_id);
            return (
              <div key={f.id} className="bg-black border border-white/5 group" data-testid={`film-${f.id}`}>
                <div className="aspect-video relative overflow-hidden">
                  {ytId ? (
                    <iframe src={`https://www.youtube.com/embed/${ytId}`} title={f.title} loading="lazy"
                      className="w-full h-full" allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture" allowFullScreen />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-white/40 text-xs">Invalid YouTube ID</div>
                  )}
                </div>
                <div className="p-6">
                  <div className="text-[0.65rem] tracking-[0.3em] uppercase text-[#D4AF37]">{f.category}</div>
                  <h3 className="font-serif text-2xl mt-2">{f.title}</h3>
                  {f.description && <p className="text-sm text-white/55 mt-2">{f.description}</p>}
                </div>
              </div>
            );
          })}
        </div>
        <div className="text-center mt-12">
          <Link to="/films" className="btn-gold" data-testid="films-cta">All Wedding Films <Play size={14}/></Link>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Process ---------------- */
export function Process({ content }) {
  const [steps, setSteps] = useState([]);
  useEffect(() => { api.get("/process-steps").then(r => setSteps(r.data)).catch(()=>{}); }, []);
  if (!steps.length) return null;
  return (
    <section className="section-pad bg-[#050505]" data-testid="process-section">
      <div className="max-w-[1400px] mx-auto">
        <div className="mb-20">
          <SectionHeader content={content}
            defaults={{ eyebrow: "Our Process", headlineHtml: "From Hello to <em>Heirloom</em>" }} />
        </div>
        <div className="relative">
          <div className="absolute left-4 md:left-1/2 top-0 bottom-0 w-px bg-white/10" />
          {steps.map((s, i) => (
            <div key={s.id || i} className={`relative flex items-start md:items-center mb-12 ${i % 2 ? "md:flex-row-reverse" : ""}`}>
              <div className="absolute left-4 md:left-1/2 w-3 h-3 -translate-x-1/2 bg-[#D4AF37] rounded-full" />
              <div className="ml-12 md:ml-0 md:w-1/2 md:px-12">
                <div className="font-serif text-4xl text-[#D4AF37]/30">{String(s.step_number ?? (i+1)).padStart(2,"0")}</div>
                <h3 className="font-serif text-2xl mt-1">{s.title}</h3>
                <p className="text-white/55 text-sm mt-2 max-w-sm">{s.description}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- Testimonials Carousel ---------------- */
export function TestimonialsCarousel({ content }) {
  const [items, setItems] = useState([]);
  const [idx, setIdx] = useState(0);
  useEffect(() => { api.get("/testimonials").then(r => setItems(r.data.filter(t => t.featured) .length ? r.data.filter(t=>t.featured) : r.data)).catch(()=>{}); }, []);
  if (!items.length) return null;
  const t = items[idx];
  return (
    <section className="section-pad bg-[#0a0a0a] border-y border-white/5 relative overflow-hidden" data-testid="testimonials-carousel">
      <img src="https://images.unsplash.com/photo-1503525443530-339273ca8a86" className="absolute inset-0 w-full h-full object-cover opacity-10" alt="" />
      <div className="max-w-3xl mx-auto text-center relative">
        {(content?.eyebrow || content?.headline_html) && (
          <div className="mb-10">
            <SectionHeader content={content} defaults={{ eyebrow: "Stories From Couples", headlineHtml: "Words From the <em>Couples We've Met</em>" }}/>
          </div>
        )}
        <Quote size={48} className="text-[#D4AF37] mx-auto mb-8" />
        <div className="flex justify-center mb-6">
          {[...Array(t.rating)].map((_,i) => <Star key={i} size={16} className="text-[#D4AF37] fill-[#D4AF37]" />)}
        </div>
        <p className="font-serif text-2xl md:text-3xl font-light leading-relaxed italic">"{t.review}"</p>
        {t.photo_url && <img src={t.photo_url} alt={t.name} className="w-16 h-16 rounded-full object-cover mx-auto mt-10 border border-[#D4AF37]" />}
        <div className="mt-5 font-serif text-xl">{t.name}</div>
        {t.venue && <div className="text-[0.7rem] tracking-[0.25em] uppercase text-white/50 mt-1">{t.role} · {t.venue}</div>}
        <div className="flex justify-center gap-3 mt-10">
          <button onClick={() => setIdx((idx - 1 + items.length) % items.length)} className="h-10 w-10 border border-white/20 hover:border-[#D4AF37] hover:text-[#D4AF37] transition flex items-center justify-center" data-testid="testimonial-prev"><ChevronLeft size={16}/></button>
          <button onClick={() => setIdx((idx + 1) % items.length)} className="h-10 w-10 border border-white/20 hover:border-[#D4AF37] hover:text-[#D4AF37] transition flex items-center justify-center" data-testid="testimonial-next"><ChevronRight size={16}/></button>
        </div>
      </div>
    </section>
  );
}

/* ---------------- Google Reviews ---------------- */
export function GoogleReviews({ content }) {
  const [data, setData] = useState({ rating: 5, count: 0, review_url: "", reviews: [] });
  useEffect(() => { api.get("/reviews/google").then(r => setData(r.data)).catch(()=>{}); }, []);
  return (
    <section className="section-pad bg-[#050505]" data-testid="google-reviews">
      <div className="max-w-[1400px] mx-auto">
        <div className="grid lg:grid-cols-3 gap-12 items-start">
          <div className="lg:col-span-1 lg:sticky lg:top-32">
            <div className="eyebrow">{content?.eyebrow || "Reviews"}</div>
            <h2 className="font-serif text-4xl md:text-5xl font-light mt-4 tracking-tight"
              dangerouslySetInnerHTML={{ __html: (content?.headline_html || "Loved by <em>Couples</em>").replace(/<em>/g, '<em class="text-[#D4AF37] not-italic">') }} />
            <div className="mt-8 p-8 border border-white/10 bg-[#0a0a0a]">
              <div className="text-7xl font-serif text-[#D4AF37]">{data.rating.toFixed(1)}</div>
              <div className="flex gap-1 mt-2">{[...Array(5)].map((_,i)=>(<Star key={i} size={16} className="fill-[#D4AF37] text-[#D4AF37]" />))}</div>
              <div className="text-sm text-white/60 mt-3">{data.count}+ Google Reviews</div>
              {data.review_url && <a href={data.review_url} target="_blank" rel="noreferrer" className="btn-gold mt-6 w-full" data-testid="leave-google-review">Leave a Google Review</a>}
            </div>
          </div>
          <div className="lg:col-span-2 grid sm:grid-cols-2 gap-5">
            {data.reviews.map((r, i) => (
              <div key={i} className="bg-[#0a0a0a] border border-white/10 p-6 flex flex-col" data-testid={`google-review-${i}`}>
                <div className="flex gap-1 mb-3">{[...Array(r.rating)].map((_,j)=>(<Star key={j} size={12} className="fill-[#D4AF37] text-[#D4AF37]"/>))}</div>
                <p className="text-sm text-white/75 leading-relaxed mb-4">"{r.text}"</p>
                <div className="flex justify-between items-center text-xs mt-auto">
                  <div className="font-serif text-base text-white">{r.author}</div>
                  <div className="text-white/40">{r.relative_time}</div>
                </div>
                {data.review_url && (
                  <a href={data.review_url} target="_blank" rel="noreferrer"
                    data-testid={`google-review-${i}-view-google`}
                    className="mt-4 pt-4 border-t border-white/10 flex items-center justify-center gap-2 text-[0.6rem] tracking-[0.25em] uppercase text-white/60 hover:text-[#D4AF37] transition">
                    View on Google <ArrowRight size={10} />
                  </a>
                )}
              </div>
            ))}
          </div>
        </div>
        <p className="text-center text-xs text-white/40 mt-12 max-w-2xl mx-auto">
          Website testimonials submitted here are reviewed by our team before appearing on this site.
          They are not automatically posted to Google. To share your story on Google, please use the link above.
        </p>
      </div>
    </section>
  );
}

/* ---------------- Availability Form ---------------- */
export function AvailabilityForm({ id = "availability", content }) {
  const [f, setF] = useState({ name:"", email:"", phone:"", wedding_date:"", venue:"", city:"", photography:true, videography:true });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/leads", { ...f, source: "availability" });
      toast.success("We'll be in touch within 24 hours.");
      setF({ name:"", email:"", phone:"", wedding_date:"", venue:"", city:"", photography:true, videography:true });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally { setBusy(false); }
  };
  return (
    <section id={id} className="section-pad bg-[#0a0a0a] border-y border-white/5" data-testid="availability-form-section">
      <div className="max-w-3xl mx-auto mb-12">
        <SectionHeader content={content}
          defaults={{ eyebrow: "Check Availability", headlineHtml: "Tell Us About <em>Your Day</em>",
                      body: "Share a few details about your wedding. We'll get back to you within a day with availability and a thoughtful next step." }}/>
      </div>
      <form onSubmit={submit} className="max-w-3xl mx-auto grid sm:grid-cols-2 gap-5" data-testid="availability-form">
        {[
          ["name","Full Name *","text", true],
          ["email","Email *","email", true],
          ["phone","Phone *","tel", true],
          ["wedding_date","Wedding Date","date", false],
          ["venue","Venue","text", false],
          ["city","City","text", false],
        ].map(([k,label,type,req]) => (
          <label key={k} className="block">
            <span className="text-[0.65rem] tracking-[0.25em] uppercase text-white/60">{label}</span>
            <input type={type} required={req} value={f[k]} onChange={(e)=>setF({...f,[k]:e.target.value})}
              data-testid={`avail-${k}`}
              className="mt-2 w-full bg-black border border-white/15 px-4 py-3 text-sm focus:border-[#D4AF37] outline-none transition" />
          </label>
        ))}
        <div className="sm:col-span-2 flex flex-wrap gap-6 mt-2">
          <label className="flex items-center gap-2 text-sm text-white/80">
            <input type="checkbox" checked={f.photography} onChange={(e)=>setF({...f,photography:e.target.checked})} data-testid="avail-photography" className="accent-[#D4AF37]" />
            Photography Needed
          </label>
          <label className="flex items-center gap-2 text-sm text-white/80">
            <input type="checkbox" checked={f.videography} onChange={(e)=>setF({...f,videography:e.target.checked})} data-testid="avail-videography" className="accent-[#D4AF37]" />
            Videography Needed
          </label>
        </div>
        <div className="sm:col-span-2 flex flex-col sm:flex-row gap-4 items-start sm:items-center mt-2">
          <button type="submit" disabled={busy} className="btn-gold w-full sm:w-auto" data-testid="avail-submit">
            {busy ? "Sending..." : "Check Availability"} <ArrowRight size={14}/>
          </button>
          <span className="text-xs text-white/40">or</span>
          <Link to="/book" className="btn-ghost w-full sm:w-auto" data-testid="avail-book-link">Book a 15-min Call <ArrowRight size={14}/></Link>
        </div>
      </form>
    </section>
  );
}

/* ---------------- Packages ---------------- */
export function Packages({ content }) {
  const [pkgs, setPkgs] = useState([]);
  useEffect(() => { api.get("/packages").then(r => setPkgs(r.data)).catch(()=>{}); }, []);
  return (
    <section className="section-pad bg-[#050505]" data-testid="packages-section">
      <div className="max-w-[1400px] mx-auto">
        <div className="mb-16">
          <SectionHeader content={content}
            defaults={{ eyebrow: "Collections", headlineHtml: "Choose What Feels <em>Right</em>" }}/>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {pkgs.map(p => (
            <div key={p.id} className={`p-10 border ${p.highlighted ? "border-[#D4AF37] bg-[#0a0a0a]" : "border-white/10 bg-[#0a0a0a]"}`} data-testid={`package-${p.name.toLowerCase()}`}>
              {p.highlighted && <div className="eyebrow mb-3">Most Popular</div>}
              <h3 className="font-serif text-3xl">{p.name}</h3>
              <div className="font-serif text-5xl text-[#D4AF37] mt-3">{p.price}</div>
              <p className="text-sm text-white/55 mt-3">{p.description}</p>
              <ul className="mt-8 space-y-3">
                {p.features.map((f, i) => (
                  <li key={i} className="flex gap-2 text-sm text-white/80"><CheckCircle2 size={14} className="text-[#D4AF37] mt-0.5 shrink-0" />{f}</li>
                ))}
              </ul>
              <Link to="/contact" className={p.highlighted ? "btn-gold w-full mt-10" : "btn-ghost w-full mt-10"} data-testid={`pkg-cta-${p.name.toLowerCase()}`}>Inquire</Link>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

/* ---------------- FAQ Accordion ---------------- */
export function FaqSection({ limit, content }) {
  const [faqs, setFaqs] = useState([]);
  const [open, setOpen] = useState(null);
  useEffect(() => { api.get("/faqs").then(r => setFaqs(r.data)).catch(()=>{}); }, []);
  const shown = limit ? faqs.slice(0, limit) : faqs;
  return (
    <section className="section-pad bg-[#0a0a0a] border-y border-white/5" data-testid="faq-section">
      <div className="max-w-3xl mx-auto">
        <div className="mb-12">
          <SectionHeader content={content}
            defaults={{ eyebrow: "FAQ", headlineHtml: "Frequently <em>Asked</em>" }}/>
        </div>
        <div className="space-y-px bg-white/10">
          {shown.map((f, i) => (
            <div key={f.id} className="bg-[#0a0a0a]" data-testid={`faq-item-${i}`}>
              <button onClick={() => setOpen(open === i ? null : i)} className="w-full text-left px-6 py-5 flex justify-between items-center gap-4 hover:bg-[#111] transition">
                <span className="font-serif text-lg">{f.question}</span>
                <span className="text-[#D4AF37] text-2xl shrink-0">{open === i ? "−" : "+"}</span>
              </button>
              {open === i && <div className="px-6 pb-6 text-sm text-white/65 leading-relaxed">{f.answer}</div>}
            </div>
          ))}
        </div>
        {limit && (
          <div className="text-center mt-10">
            <Link to="/faq" className="btn-ghost">All FAQs</Link>
          </div>
        )}
      </div>
    </section>
  );
}

/* ---------------- CTA Strip ---------------- */
export function CtaStrip({ content }) {
  const headlineHtml = (content?.headline_html || "Let's Create Something <em>Timeless</em>")
    .replace(/<em>/g, '<em class="text-[#D4AF37] not-italic">');
  return (
    <section className="relative py-24 px-6 bg-black overflow-hidden" data-testid="cta-strip">
      <img src="https://images.unsplash.com/photo-1525772764200-be829a350797?auto=format&fit=crop&w=2000&q=80" alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
      <div className="relative max-w-3xl mx-auto text-center">
        {content?.eyebrow && <div className="eyebrow mb-3">{content.eyebrow}</div>}
        <h2 className="font-serif text-4xl md:text-5xl font-light tracking-tight"
            dangerouslySetInnerHTML={{ __html: headlineHtml }} />
        <p className="text-white/70 mt-4">{content?.body || "We only take on a small number of weddings each season so every couple gets our full attention."}</p>
        <div className="flex flex-wrap justify-center gap-3 mt-8">
          {(content?.primary_cta_label !== "") && (
            <Link to={content?.primary_cta_link || "/contact"} className="btn-gold inline-flex">
              {content?.primary_cta_label || "Say Hello"} <ArrowRight size={14}/>
            </Link>
          )}
          {content?.secondary_cta_label && (
            <Link to={content?.secondary_cta_link || "/book"} className="btn-ghost inline-flex">
              {content.secondary_cta_label} <ArrowRight size={14}/>
            </Link>
          )}
        </div>
      </div>
    </section>
  );
}
